import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { Tooltip } from "antd";
import moment from "moment-timezone";

import { selectCanAccessAgenticPlatform } from "../../../store/selector";
import { GRAY_300, GRAY_500, PRIMARY_600, WHITE, AGENTIC_SHIMMER, AGENTIC_GLOW } from "../../../constant/colors";
import { useApi, ApiNames } from "../../../hooks/api";
import { useGetSetting } from "../../../hooks/getSetting";
import AgenticRunsModal from "./AgenticRunsModal";
import { RUN_STATUS_META } from "./runStatuses";

// ─── Visual identity ────────────────────────────────────────────────────
//
// At rest, the button is a white chip with a 1px gray border — the same
// dimensional footprint as `ManageConsentButton` (`150 × 32`, `5px`
// radius) so it slots into the patient-header action column without
// breaking the row's visual rhythm.
//
// The AI identity reveals on interaction and through fine motion:
//
//   1. **Sparkle icon in PRIMARY_600 with a slow pulse** — the
//      four-point sparkle is the de-facto "AI" glyph; the 2.6s breath
//      cadence is barely noticeable in peripheral vision but
//      unmistakable on focus. Colored in the AI indigo so the white
//      button still telegraphs "this is the agentic surface".
//   2. **Shimmer sweep on hover** — a diagonal indigo highlight that
//      sweeps across the button on pointer-enter, turning the click
//      target into a moment of delight only when the navigator is
//      already attending to it.
//   3. **Status-colored label** — the run's status drives the label
//      text color (success / warning / error / processing / neutral)
//      using the SAME palette as the modal's `<Tag>`. No status yet →
//      neutral gray + static "Agentic run" label.
//
// All status colors come from the shared `runStatuses.js` map so the
// button + modal stay in lockstep.

const sparklePulse = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
  50% { transform: scale(1.18) rotate(8deg); opacity: 0.85; }
`;

const shimmerSweep = keyframes`
  0% { transform: translateX(-120%) skewX(-20deg); }
  100% { transform: translateX(220%) skewX(-20deg); }
`;

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 150px;
  /* Breathing room from the preceding action button (Manage Consent)
     so the agentic surface isn't crammed against its sibling. Matches
     the spacing rhythm of the other vertical gaps in the patient-header
     action column. */
  margin-top: 8px;
`;

const StyledButton = styled.button`
  /* White chip with a 1px gray border — same footprint as the sibling
     action buttons (ManageConsent / ManagePatientDetails) so the row
     still reads as one cluster. Text color is driven by the run's
     status (see the labelHex prop); the sparkle carries the AI identity. */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 150px;
  height: 32px;
  padding: 7px;
  border: 1px solid ${GRAY_300};
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  font-family: "Open Sans", sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.5em;
  color: ${({ labelHex }) => labelHex};
  background-color: ${WHITE};
  box-sizing: border-box;

  transition: filter 120ms ease, border-color 120ms ease;

  /* Hover: faint indigo border tint so the AI affordance shows up
     under the pointer without flashing a heavy color change. No
     transform / no glow expansion: we don't want the button to
     physically pop out of the cluster. */
  &:hover {
    border-color: ${PRIMARY_600};
  }

  &:active {
    filter: brightness(0.96);
  }

  /* Shimmer overlay — diagonal indigo sheen that sweeps across only
     on hover. Persistent state stays calm; the AI moment is reserved
     for the pointer-enter. */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, ${AGENTIC_SHIMMER} 50%, transparent 100%);
    transform: translateX(-120%) skewX(-20deg);
    pointer-events: none;
  }

  &:hover::before {
    animation: ${shimmerSweep} 900ms ease-out;
  }
`;

const Sparkle = styled.svg`
  flex-shrink: 0;
  animation: ${sparklePulse} 2.6s ease-in-out infinite;
  transform-origin: 50% 50%;
  filter: drop-shadow(0 0 3px ${AGENTIC_GLOW});
`;

const Label = styled.span`
  position: relative;
  z-index: 1;
`;

/**
 * Self-contained entry point for the agentic-platform audit modal.
 *
 * Renders nothing for non-TMComplete users — this is a TM-internal tool;
 * customer-employed navigators don't see it. Owns its own modal-visibility
 * state so callers just drop the component into their action area
 * (`<AgenticRunsButton patientId={patientId} />`), no plumbing required.
 *
 * Renders nothing until the latest-run fetch confirms the patient HAS a run —
 * the agentic surface only shows for patients the agent has touched (no run →
 * no button). When a run exists, the label reflects its status — "Needs
 * review" / "Consent sent" / "Blocked" / etc. Fetched via
 * `/latest-run-details` on mount (for the label), and REFETCHED on every
 * button click so the modal opens on the run as it is now — an in-flight
 * run's mount-time payload can be minutes stale.
 *
 * Visual identity uses an indigo→violet gradient + animated sparkle to
 * signal "AI feature" — distinct from the deterministic action buttons
 * (Manage Consent, Manage Patient Details) in the same header cluster.
 */
const AgenticRunsButton = ({ patientId }) => {
  const { t } = useTranslation();
  const canAccess = useSelector(selectCanAccessAgenticPlatform);
  // Gated by the per-site `tmAgenticPlatformEnabled` setting (same gate as the
  // manual-trigger button + the /tm-agentic-platform page): the agentic surface
  // is hidden entirely on sites where the feature is off.
  const [enabled] = useGetSetting(["tmAgenticPlatformEnabled"]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fetchRunDetails, runResult, isFetching, error] = useApi(ApiNames.AgenticLatestRunDetails);

  // `useApi` returns a fresh `fetchRunDetails` identity each render, so call it
  // via a ref and keep it out of the dep array below (adding it would loop).
  const fetchRunDetailsRef = useRef(fetchRunDetails);
  fetchRunDetailsRef.current = fetchRunDetails;

  // Eagerly fetch the patient's latest run (with steps) once on patient-page
  // render — the label reflects the current bucket. (Opening the modal
  // refetches; see the button onClick.) Only runs for allowed users
  // (roles / TMC) on enabled sites.
  useEffect(() => {
    if (canAccess && enabled && patientId) {
      fetchRunDetailsRef.current(patientId);
    }
  }, [canAccess, enabled, patientId]);

  if (!canAccess || !enabled) {
    return null;
  }

  // No run for this patient → render nothing. The agentic surface only appears
  // once the latest-run fetch confirms the agent has actually touched this
  // patient. `runResult` is undefined while fetching and on fetch error, so a
  // patient with no run never flashes a generic button.
  if (!runResult) {
    return null;
  }

  const status = runResult?.status ?? null;
  const meta = status ? RUN_STATUS_META[status] : null;
  const buttonText = meta ? t(meta.labelKey, meta.fallback) : t("agentic_runs.open_button");
  // No status yet (agent has never touched this patient) → neutral gray
  // label. With a status, the label adopts the bucket's color so the
  // button's verdict matches the modal's `<Tag>` at a glance.
  const labelHex = meta ? meta.hex : GRAY_500;
  // Hovering the chip surfaces WHEN the agent last ran — the latest run's start
  // time (fallback: its completion time); no run yet → a "hasn't run" hint.
  const ranAt = runResult?.createdAt || runResult?.completedAt;
  const tooltipTitle = ranAt
    ? t("agentic_runs.ran_at", {
        // App-wide moment default is UTC (index.js); `.local()` renders the
        // run time in the navigator's timezone.
        date: moment(ranAt).local().format("MMM D, YYYY [at] h:mm A")
      })
    : t("agentic_runs.never_ran");

  return (
    <>
      <Container id="agentic_runs_button">
        <Tooltip title={tooltipTitle}>
          <StyledButton
            onClick={() => {
              // Refetch on open: the mount-time payload can be minutes old for
              // an in-flight run — the modal should show the run as it is NOW.
              fetchRunDetailsRef.current(patientId);
              setModalOpen(true);
            }}
            data-testid="agentic-runs-modal-trigger"
            labelHex={labelHex}
          >
            {/* Inline sparkle SVG — primary 4-point star + two smaller
              satellites positioned asymmetrically for a hand-placed feel
              rather than a geometric grid. The icon pulses on a 2.6s
              breath cadence to telegraph "alive" without being noisy. */}
            <Sparkle width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 0.5L9.4 6.6L15.5 8L9.4 9.4L8 15.5L6.6 9.4L0.5 8L6.6 6.6L8 0.5Z" fill={PRIMARY_600} />
              <path
                d="M13 1.5L13.5 3.5L15.5 4L13.5 4.5L13 6.5L12.5 4.5L10.5 4L12.5 3.5L13 1.5Z"
                fill={PRIMARY_600}
                opacity="0.85"
              />
              <path
                d="M3 9.5L3.4 10.9L4.8 11.3L3.4 11.7L3 13.1L2.6 11.7L1.2 11.3L2.6 10.9L3 9.5Z"
                fill={PRIMARY_600}
                opacity="0.7"
              />
            </Sparkle>
            <Label>{buttonText}</Label>
          </StyledButton>
        </Tooltip>
      </Container>
      <AgenticRunsModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        patientId={patientId}
        run={runResult ?? null}
        isFetching={isFetching}
        error={error}
        onRefresh={() => fetchRunDetails(patientId)}
      />
    </>
  );
};

export default AgenticRunsButton;
export { AgenticRunsButton };
